import React from 'react';
import { Badge, Button, Table } from '@parsina/framework';
import { Tag } from 'lucide-react';

import { useTranslation } from '../../i18n';
import { EMPTY, formatUsd } from '../../lib/format';
import { num } from '../../lib/aiPricingView';
import { Code, CountCell, UsdCell, useEnumLabel } from './shared';

const STATUS_VARIANT = { active: 'success', deprecated: 'outline', unknown: 'destructive' };

/**
 * The catalogue as a table (design §12.4). `status='unknown'` rows carry the
 * danger badge «بدون قیمت» and a PRIMARY «تعیین قیمت» — every other row's
 * price action is quiet, because there the price exists and the action is a
 * correction.
 */
export default function ModelsTable({ rows, view, onSort, onOpen, onPrice, empty }) {
  const { t, formatDate, formatNumber } = useTranslation();
  const usd = (v) => formatUsd(v, { formatNumber, t });
  const providerLabel = useEnumLabel('aiPricing.models.provider');
  const modalityLabel = useEnumLabel('aiPricing.models.modality');
  const statusLabel = useEnumLabel('aiPricing.models.status');

  const audio = (price) => {
    if (!price) return EMPTY;
    if (num(price.audioUsdPerMinute) != null) {
      return t('aiPricing.models.audioPerMinute', { value: usd(price.audioUsdPerMinute) });
    }
    if (num(price.audioInputUsdPerM) != null) {
      return t('aiPricing.models.audioPerM', { value: usd(price.audioInputUsdPerM) });
    }
    return EMPTY;
  };

  const columns = [
    {
      key: 'model',
      header: t('aiPricing.models.columns.model'),
      render: (r) => (
        <div className="min-w-0">
          <div className="truncate font-semibold text-foreground"><Code title={r.modelId}>{r.modelId}</Code></div>
          {r.displayName ? <div dir="auto" className="truncate text-xs text-muted-foreground">{r.displayName}</div> : null}
        </div>
      ),
    },
    { key: 'provider', header: t('aiPricing.models.columns.provider'), width: '7rem', render: (r) => providerLabel(r.provider) },
    {
      key: 'modalities',
      header: t('aiPricing.models.columns.modalities'),
      width: '11rem',
      render: (r) => (
        <span className="inline-flex flex-wrap gap-1">
          {(r.modalities || []).map((m) => <Badge key={m} variant="outline">{modalityLabel(m)}</Badge>)}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('aiPricing.models.columns.status'),
      width: '7rem',
      render: (r) => <Badge variant={STATUS_VARIANT[r.status] || 'outline'}>{statusLabel(r.status)}</Badge>,
    },
    { key: 'input', header: t('aiPricing.models.columns.input'), align: 'end', width: '7rem', render: (r) => <UsdCell value={r.currentPrice?.inputUsdPerM} /> },
    { key: 'output', header: t('aiPricing.models.columns.output'), align: 'end', width: '7rem', render: (r) => <UsdCell value={r.currentPrice?.outputUsdPerM} /> },
    { key: 'audio', header: t('aiPricing.models.columns.audio'), align: 'end', width: '7rem', render: (r) => <span className="tabular-nums">{audio(r.currentPrice)}</span> },
    { key: 'since', header: t('aiPricing.models.columns.since'), width: '8rem', render: (r) => (r.currentPrice?.effectiveFrom ? formatDate(r.currentPrice.effectiveFrom) : EMPTY) },
    { key: 'calls', header: t('aiPricing.models.columns.calls'), sortable: true, align: 'end', width: '7rem', render: (r) => <CountCell value={r.callsInWindow} /> },
    { key: 'cost', header: t('aiPricing.models.columns.cost'), sortable: true, align: 'end', width: '8rem', render: (r) => <UsdCell value={r.costUsdInWindow} /> },
    {
      key: 'actions',
      header: <span className="sr-only">{t('aiPricing.models.columns.actions')}</span>,
      align: 'end',
      width: '9rem',
      render: (r) => (
        <Button
          variant={r.status === 'unknown' ? 'default' : 'ghost'}
          size="sm"
          onClick={(e) => { e.stopPropagation(); onPrice(r); }}
          data-testid={`ai-models-row-${r.modelId}-price`}
        >
          <Tag size={14} aria-hidden />
          {r.status === 'unknown' ? t('aiPricing.models.setPrice') : t('aiPricing.models.updatePrice')}
        </Button>
      ),
    },
  ];

  return (
    <Table
      dense
      ariaLabel={t('aiPricing.models.tableAria')}
      columns={columns}
      rows={rows}
      rowKey={(r) => r.modelId}
      onRowActivate={onOpen}
      rowProps={(r) => ({ 'data-testid': `ai-models-row-${r.modelId}`, 'aria-label': t('aiPricing.models.open', { model: r.modelId }) })}
      sort={{ key: view.sort || 'cost', dir: view.dir || 'desc' }}
      onSort={onSort}
      empty={empty}
      data-testid="ai-models-table"
    />
  );
}
